import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography, Card, CardMedia, CardContent, Chip, CircularProgress } from '@mui/material';
import {
  Download,
  PictureAsPdf,
  Description,
  Image,
  AudioFile,
  VideoFile
} from '@mui/icons-material';
import { Message } from '../types/Message';
import api from '../services/api';

// Base URL do backend para arquivos de mídia
const BACKEND_BASE_URL = import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';

interface MediaMessageProps {
  message: Message;
  isOutbound: boolean;
}

// Hook para buscar mídia autenticada e retornar blob URL
function useAuthenticatedMedia(mediaUrl: string | null | undefined): { src: string | undefined; loading: boolean } {
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!mediaUrl) return;

    // URLs externas (http/https) não precisam de auth
    if (mediaUrl.startsWith('http')) {
      setSrc(mediaUrl);
      return;
    }

    // URL relativa do proxy — buscar via axios com auth
    // Remove o prefixo /api/v1 pois o axios já tem baseURL com esse prefixo
    const path = mediaUrl.replace(/^\/api\/v1/, '');
    let objectUrl: string;
    setLoading(true);
    setSrc(undefined);

    api.get(path, { responseType: 'blob' })
      .then(response => {
        objectUrl = URL.createObjectURL(response.data);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(undefined))
      .finally(() => setLoading(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaUrl]);

  return { src, loading };
}

// Baixar blob como arquivo
function downloadBlob(src: string, filename: string) {
  const a = document.createElement('a');
  a.href = src;
  a.download = filename || 'arquivo';
  a.click();
}

const MediaMessage: React.FC<MediaMessageProps> = ({ message, isOutbound }) => {
  const { media_type, media_url, content } = message;

  // URL completa do arquivo de mídia (pode ser relativa /api/... ou absoluta)
  const rawMediaUrl = media_url
    ? (media_url.startsWith('http') ? media_url : media_url.startsWith('/api/') ? media_url : `${BACKEND_BASE_URL}${media_url}`)
    : undefined;

  const { src: authenticatedSrc, loading } = useAuthenticatedMedia(rawMediaUrl);

  // Player de áudio
  if (media_type === 'audio') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          background: isOutbound ? 'rgba(255,255,255,0.15)' : 'rgba(99,102,241,0.08)',
          borderRadius: 2,
          mb: 1,
          minWidth: 240
        }}
      >
        {loading && <CircularProgress size={20} sx={{ color: isOutbound ? '#fff' : '#6366f1' }} />}
        {authenticatedSrc && (
          <audio
            controls
            src={authenticatedSrc}
            style={{ height: 40, maxWidth: 280, borderRadius: 8 }}
          />
        )}
        {authenticatedSrc && (
          <IconButton
            size="small"
            onClick={() => downloadBlob(authenticatedSrc, 'audio.ogg')}
            sx={{
              color: isOutbound ? '#fff' : '#6366f1',
              background: isOutbound ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.15)',
              '&:hover': {
                background: isOutbound ? 'rgba(255,255,255,0.3)' : 'rgba(99,102,241,0.25)'
              }
            }}
          >
            <Download fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  // Imagem clicável
  if (media_type === 'image') {
    return (
      <Box sx={{ mb: 1 }}>
        {loading && (
          <Box sx={{ width: 120, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ color: isOutbound ? '#fff' : '#6366f1' }} />
          </Box>
        )}
        {authenticatedSrc && (
          <Card
            sx={{
              maxWidth: 300,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: isOutbound ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              position: 'relative'
            }}
            onClick={() => window.open(authenticatedSrc, '_blank')}
          >
            <CardMedia
              component="img"
              image={authenticatedSrc}
              alt={content}
              sx={{ height: 200, objectFit: 'cover', cursor: 'pointer' }}
            />
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); downloadBlob(authenticatedSrc, 'imagem.jpg'); }}
                sx={{
                  background: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  '&:hover': { background: '#fff' }
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Box>
            {content && content !== '[Imagem]' && (
              <CardContent sx={{ p: 1, background: 'rgba(255,255,255,0.95)' }}>
                <Typography variant="caption" sx={{ color: '#1f2937', fontSize: 12 }}>
                  {content}
                </Typography>
              </CardContent>
            )}
          </Card>
        )}
      </Box>
    );
  }

  // Documento
  if (media_type === 'document') {
    let filename = content || 'documento';
    if (content && content.startsWith('[Documento:')) {
      filename = content.replace('[Documento: ', '').replace(']', '');
    }

    const fileExtension = filename.split('.').pop()?.toLowerCase() || '';
    const getIcon = () => {
      switch (fileExtension) {
        case 'pdf': return <PictureAsPdf sx={{ fontSize: 32, color: '#ef4444' }} />;
        case 'doc':
        case 'docx': return <Description sx={{ fontSize: 32, color: '#2563eb' }} />;
        case 'xls':
        case 'xlsx': return <Description sx={{ fontSize: 32, color: '#16a34a' }} />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif': return <Image sx={{ fontSize: 32, color: '#8b5cf6' }} />;
        default: return <Description sx={{ fontSize: 32, color: '#6b7280' }} />;
      }
    };

    return (
      <Box sx={{ mb: 1 }}>
        <Card
          sx={{
            maxWidth: 300,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            boxShadow: isOutbound ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
            cursor: authenticatedSrc ? 'pointer' : 'default',
            '&:hover': {
              background: authenticatedSrc
                ? (isOutbound ? 'rgba(255,255,255,0.1)' : 'rgba(99,102,241,0.08)')
                : undefined
            }
          }}
          onClick={() => authenticatedSrc && window.open(authenticatedSrc, '_blank')}
        >
          <Box sx={{
            width: 48, height: 48, borderRadius: 1.5,
            background: isOutbound ? 'rgba(255,255,255,0.15)' : 'rgba(99,102,241,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            {loading ? <CircularProgress size={20} /> : getIcon()}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isOutbound ? '#fff' : '#1f2937',
                fontSize: 13,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {filename}
            </Typography>
            {fileExtension && (
              <Chip
                label={fileExtension.toUpperCase()}
                size="small"
                sx={{
                  height: 18, fontSize: 10,
                  background: isOutbound ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.15)',
                  color: isOutbound ? '#fff' : '#6366f1',
                  mt: 0.5
                }}
              />
            )}
          </Box>
          {authenticatedSrc && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); downloadBlob(authenticatedSrc, filename); }}
              sx={{
                color: isOutbound ? '#fff' : '#6366f1',
                background: isOutbound ? 'rgba(255,255,255,0.2)' : 'rgba(99,102,241,0.15)',
                '&:hover': {
                  background: isOutbound ? 'rgba(255,255,255,0.3)' : 'rgba(99,102,241,0.25)'
                }
              }}
            >
              <Download fontSize="small" />
            </IconButton>
          )}
        </Card>
      </Box>
    );
  }

  // Vídeo
  if (media_type === 'video') {
    return (
      <Box sx={{ mb: 1 }}>
        {loading && (
          <Box sx={{ width: 200, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: isOutbound ? '#fff' : '#6366f1' }} />
          </Box>
        )}
        {authenticatedSrc && (
          <Card sx={{ maxWidth: 300, borderRadius: 2, overflow: 'hidden', boxShadow: isOutbound ? 'none' : '0 2px 8px rgba(0,0,0,0.1)', position: 'relative' }}>
            <video
              controls
              src={authenticatedSrc}
              style={{ width: '100%', maxHeight: 200, display: 'block' }}
            />
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <IconButton
                size="small"
                onClick={() => downloadBlob(authenticatedSrc, 'video.mp4')}
                sx={{
                  background: 'rgba(255,255,255,0.95)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  '&:hover': { background: '#fff' }
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Box>
            {content && content !== '[Vídeo]' && (
              <CardContent sx={{ p: 1, background: 'rgba(255,255,255,0.95)' }}>
                <Typography variant="caption" sx={{ color: '#1f2937', fontSize: 12 }}>
                  {content}
                </Typography>
              </CardContent>
            )}
          </Card>
        )}
      </Box>
    );
  }

  // Sticker
  if (media_type === 'sticker') {
    return (
      <Box sx={{ mb: 1 }}>
        {loading && <CircularProgress size={20} sx={{ color: isOutbound ? '#fff' : '#6366f1' }} />}
        {authenticatedSrc && (
          <Box
            component="img"
            src={authenticatedSrc}
            alt="Sticker"
            sx={{
              maxWidth: 150, maxHeight: 150, borderRadius: 1, cursor: 'pointer'
            }}
            onClick={() => window.open(authenticatedSrc, '_blank')}
          />
        )}
      </Box>
    );
  }

  // Fallback para mídia não reconhecida
  if (rawMediaUrl) {
    return (
      <Box sx={{ mb: 1 }}>
        <Card
          sx={{
            maxWidth: 300,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: isOutbound ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
            cursor: authenticatedSrc ? 'pointer' : 'default'
          }}
          onClick={() => authenticatedSrc && window.open(authenticatedSrc, '_blank')}
        >
          {loading
            ? <CircularProgress size={24} />
            : <Description sx={{ fontSize: 32, color: isOutbound ? '#fff' : '#6b7280' }} />
          }
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: isOutbound ? '#fff' : '#1f2937',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              Arquivo de mídia
            </Typography>
          </Box>
          {authenticatedSrc && (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); downloadBlob(authenticatedSrc, 'arquivo'); }}
              sx={{ color: isOutbound ? '#fff' : '#6366f1' }}
            >
              <Download fontSize="small" />
            </IconButton>
          )}
        </Card>
      </Box>
    );
  }

  return null;
};

export default MediaMessage;
